import React, { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { getSession } from '../lib/session';
import {
  getPushState,
  subscribePush,
  unsubscribePush,
  isPushSupported,
} from '../lib/push';

/**
 * Opt-in card that surfaces on the Moments tab. Renders as a compact banner
 * with a single primary action:
 *   - default        Enable notifications (asks browser permission + subscribes)
 *   - granted-on     Notifications on. Turn off. (unsubscribes)
 *   - granted-off    Turn back on. (re-subscribes without asking permission)
 *   - blocked        Static explainer — we cannot re-ask once denied
 *   - unsupported    Hidden entirely (some browsers, iOS non-PWA)
 *
 * Not shown for signed-out members. The RPC calls need auth.uid() and the
 * user experience of "enable notifications, then we log you in first" is
 * worse than gating the card.
 */
export default function PushOptIn() {
  const [state, setState] = useState('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [signedIn, setSignedIn] = useState(false);

  const refresh = useCallback(async () => {
    // The compact session snapshot doesn't carry the access token; presence
    // of a uid is our signed-in signal (lib/session.js populates it from
    // the auth.users record on sign-in).
    const session = getSession();
    setSignedIn(Boolean(session?.uid));
    if (!isPushSupported()) {
      setState('unsupported');
      return;
    }
    const s = await getPushState();
    setState(s.state);
  }, []);

  useEffect(() => {
    refresh();
    // Re-check on tab focus in case the user changed permission via browser UI.
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    // Also respond to service-worker subscription rotations.
    const onSwMessage = (event) => {
      if (event.data?.type === 'pushsubscriptionchange') refresh();
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', onSwMessage);
    }
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage);
      }
    };
  }, [refresh]);

  const handleEnable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await subscribePush();
      setState('granted-on');
    } catch (e) {
      if (e.message === 'blocked' || e.code === 'denied') {
        setState('blocked');
      } else if (e.message === 'dismissed' || e.code === 'default') {
        // User closed the browser prompt — leave state as default so we can
        // ask again next time without nagging.
      } else if (e.message === 'not_authenticated') {
        setError('Please sign in first.');
      } else {
        setError('Could not enable notifications. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }, []);

  const handleDisable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await unsubscribePush();
      setState('default');
    } catch (e) {
      setError('Could not turn off notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  }, []);

  if (state === 'unsupported' || state === 'loading') return null;
  if (!signedIn) return null;

  const isOn = state === 'granted-on';

  return (
    <div
      className="px-4 pt-4 desktop-shell"
      data-testid="push-optin"
      data-push-state={state}
    >
      <Card
        className="overflow-hidden border-primary/20"
        data-push-state={state}
      >
        <CardContent className="p-3 flex items-center gap-3">
          <div className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${isOn ? 'bg-emerald-100 text-emerald-700' : 'bg-primary/10 text-primary'}`}>
            {isOn
              ? <BellRing className="h-5 w-5" aria-hidden />
              : state === 'blocked'
                ? <BellOff className="h-5 w-5" aria-hidden />
                : <Bell className="h-5 w-5" aria-hidden />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold leading-tight text-[#0F172A]">
              {isOn ? 'Notifications on' : 'Get MTFA updates'}
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
              {state === 'blocked' && 'Notifications are blocked in your browser. Enable them in site settings.'}
              {state === 'default' && 'Community appeals, class openings, and volunteer calls — direct to your device.'}
              {state === 'granted-off' && 'Re-enable to get published community updates on this device.'}
              {state === 'granted-on' && "You'll get MTFA's community updates on this device."}
            </p>
            {error && (
              <p className="text-[11px] text-red-600 mt-1" data-testid="push-optin-error">{error}</p>
            )}
          </div>
          <div className="shrink-0">
            {state === 'default' || state === 'granted-off' ? (
              <button
                type="button"
                data-testid="push-optin-enable"
                disabled={busy}
                onClick={handleEnable}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-primary text-primary-foreground shadow disabled:opacity-60"
              >
                {busy ? 'Enabling…' : 'Turn on'}
              </button>
            ) : null}
            {state === 'granted-on' ? (
              <button
                type="button"
                data-testid="push-optin-disable"
                disabled={busy}
                onClick={handleDisable}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-muted text-[#0F172A] border disabled:opacity-60"
              >
                {busy ? 'Turning off…' : 'Turn off'}
              </button>
            ) : null}
            {state === 'blocked' ? (
              <span
                className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-muted text-muted-foreground"
                data-testid="push-optin-blocked"
              >Blocked</span>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
